/**
 * MSK trading-day helpers (Europe/Moscow, fixed UTC+3 — same as B_day snap).
 */

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000

/** @returns {string} YYYY-MM-DD in MSK */
export function mskToday(now = new Date()) {
  const t = new Date(now.getTime() + MSK_OFFSET_MS)
  const y = t.getUTCFullYear()
  const m = String(t.getUTCMonth() + 1).padStart(2, '0')
  const d = String(t.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** @param {string} day YYYY-MM-DD */
export function monthOfDay(day) {
  return String(day || mskToday()).slice(0, 7)
}

/**
 * @param {string} day YYYY-MM-DD
 * @param {{ today?: string }} [opts]
 */
export function formatDayLabel(day, opts = {}) {
  const today = opts.today || mskToday()
  if (!day) return '—'
  if (day === today) return 'Сегодня'
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  const dt = new Date(Date.UTC(y, m - 1, d))
  const months = [
    'янв',
    'фев',
    'мар',
    'апр',
    'май',
    'июн',
    'июл',
    'авг',
    'сен',
    'окт',
    'ноя',
    'дек',
  ]
  const mon = months[m - 1] || ''
  if (y === Number(String(today).slice(0, 4))) return `${d} ${mon}`
  return `${d} ${mon} ${y}`
}

/**
 * @param {number | null | undefined} pct
 * @returns {string}
 */
export function formatPnlPct(pct) {
  if (pct == null || !Number.isFinite(Number(pct))) return ''
  const v = Number(pct)
  const sign = v > 0 ? '+' : ''
  const abs = Math.abs(v)
  const body = abs >= 10 ? abs.toFixed(0) : abs.toFixed(1)
  return `${sign}${body}%`
}

/**
 * Build calendar grid cells for a YYYY-MM month (Mon-first).
 * @param {string} month YYYY-MM
 */
export function monthGrid(month) {
  const [ys, ms] = String(month || '').split('-')
  const y = Number(ys)
  const m = Number(ms)
  if (!y || !m) return { month, weeks: [], label: month }
  const first = new Date(Date.UTC(y, m - 1, 1))
  // JS: 0=Sun … convert to Mon=0
  let startPad = (first.getUTCDay() + 6) % 7
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
  /** @type {{ day: string | null, dom: number | null }[]} */
  const cells = []
  for (let i = 0; i < startPad; i++) cells.push({ day: null, dom: null })
  for (let d = 1; d <= daysInMonth; d++) {
    const day = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ day, dom: d })
  }
  while (cells.length % 7 !== 0) cells.push({ day: null, dom: null })
  /** @type {{ day: string | null, dom: number | null }[][]} */
  const weeks = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  const months = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ]
  return { month, weeks, label: `${months[m - 1] || ''} ${y}`.trim() }
}

/** @param {string} month YYYY-MM @param {number} delta */
export function shiftMonth(month, delta) {
  const [ys, ms] = String(month || mskToday().slice(0, 7)).split('-')
  let y = Number(ys)
  let m = Number(ms) + delta
  while (m < 1) {
    m += 12
    y -= 1
  }
  while (m > 12) {
    m -= 12
    y += 1
  }
  return `${y}-${String(m).padStart(2, '0')}`
}
